import React from "react";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  listEvents,
  listLeagues,
} from "../../graphql/queries";

const client = generateClient();

const TestData: React.FC = () => {
  // Test all the queries used in PublicStandings
  const { data: playerConstructs, isLoading: constructsLoading } = useQuery({
    queryKey: ["testPlayerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  const { data: eventPlayerResults, isLoading: resultsLoading } = useQuery({
    queryKey: ["testEventPlayerResults"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });
      return result.data.listEventPlayerResults.items;
    },
  });

  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["testEvents"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listEvents,
        variables: { limit: 1000 },
      });
      return result.data.listEvents.items;
    },
  });

  const { data: leagues, isLoading: leaguesLoading } = useQuery({
    queryKey: ["testLeagues"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listLeagues,
        variables: { limit: 1000 },
      });
      return result.data.listLeagues.items;
    },
  });

  const isLoading =
    constructsLoading || resultsLoading || eventsLoading || leaguesLoading;

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg">Loading test data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Database Test Data</h1>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>PlayerConstructs</CardTitle>
            <CardDescription>
              Count: {playerConstructs?.length || 0}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {playerConstructs && playerConstructs.length > 0 ? (
              <div className="space-y-2">
                {playerConstructs.slice(0, 5).map((construct: any) => (
                  <div key={construct.id} className="p-2 border rounded">
                    <div className="font-medium">{construct.name}</div>
                    <div className="text-sm text-gray-600">
                      Events: {construct.totalEvents} | Points:{" "}
                      {construct.totalPoints}
                    </div>
                  </div>
                ))}
                {playerConstructs.length > 5 && (
                  <div className="text-sm text-gray-500">
                    ... and {playerConstructs.length - 5} more
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No PlayerConstructs found</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>EventPlayerResults</CardTitle>
            <CardDescription>
              Count: {eventPlayerResults?.length || 0}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {eventPlayerResults && eventPlayerResults.length > 0 ? (
              <div className="space-y-2">
                {eventPlayerResults.slice(0, 5).map((result: any) => (
                  <div key={result.id} className="p-2 border rounded">
                    <div className="font-medium">Rank: {result.rank}</div>
                    <div className="text-sm text-gray-600">
                      Points: {result.points} | Wins: {result.wins}-
                      {result.losses}-{result.draws}
                    </div>
                  </div>
                ))}
                {eventPlayerResults.length > 5 && (
                  <div className="text-sm text-gray-500">
                    ... and {eventPlayerResults.length - 5} more
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No EventPlayerResults found</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Events</CardTitle>
            <CardDescription>Count: {events?.length || 0}</CardDescription>
          </CardHeader>
          <CardContent>
            {events && events.length > 0 ? (
              <div className="space-y-2">
                {events.slice(0, 5).map((event: any) => (
                  <div key={event.id} className="p-2 border rounded">
                    <div className="font-medium">{event.name}</div>
                    <div className="text-sm text-gray-600">
                      {event.date} | {event.format}
                    </div>
                  </div>
                ))}
                {events.length > 5 && (
                  <div className="text-sm text-gray-500">
                    ... and {events.length - 5} more
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No Events found</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leagues</CardTitle>
            <CardDescription>Count: {leagues?.length || 0}</CardDescription>
          </CardHeader>
          <CardContent>
            {leagues && leagues.length > 0 ? (
              <div className="space-y-2">
                {leagues.slice(0, 5).map((league: any) => (
                  <div key={league.id} className="p-2 border rounded">
                    <div className="font-medium">{league.name}</div>
                    <div className="text-sm text-gray-600">
                      {league.format} | Active: {league.isActive ? "Yes" : "No"}
                    </div>
                  </div>
                ))}
                {leagues.length > 5 && (
                  <div className="text-sm text-gray-500">
                    ... and {leagues.length - 5} more
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No Leagues found</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Raw Data (Console)</CardTitle>
          <CardDescription>
            Check browser console for detailed data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600">
            Open browser console to see the raw data being returned from each
            query.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestData;

